import React, { useState, useEffect, useContext } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { useTranslation } from 'react-i18next';
import Dropdown from '../../../component/input/Dropdown';
import MultiSelect from '../../../component/input/MultiSelect';
import InputSwitch from '../../../component/input/InputSwitch';
import { PlanogramService } from '../../../service/PlanogramService';
import { MessageContext } from '../../../context/MessageContext';
import { getNextBusinessDay } from '../../../utils/date/DateUtils';
import { ReactComponent as InfoIcon } from '../../../assets/images/info-circle.svg';
import './PlanogramDeactivateDialog.scss';

const PlanogramDeactivateDialog = ({
    visible,
    onHide,
    onConfirm,
    planogramTitle,
    planogramRows,
    planogramColumns,
    planogramId
}) => {
    const { t, ready } = useTranslation();
    const { showSuccessMessage, showErrorMessage } = useContext(MessageContext);
    const [isSaving, setIsSaving] = useState(false);
    const [availableDevices, setAvailableDevices] = useState([]);
    const [isLoadingDevices, setIsLoadingDevices] = useState(false);
    const [availablePlanograms, setAvailablePlanograms] = useState([]);
    const [isLoadingPlanograms, setIsLoadingPlanograms] = useState(false);
    const [formData, setFormData] = useState({
        newPlanogram: null,
        selectedDevices: [],
        clearCells: false,
        effectiveDate: null
    });

    // Fetch available devices and planograms when dialog becomes visible
    useEffect(() => {
        let cancelled = false;
        const fetchDetails = async () => {
            if (!visible || !planogramId) return;
            setIsLoadingDevices(true);
            setIsLoadingPlanograms(true);
            try {
                const response = await PlanogramService.getDeactivatedParentPlanogramDetails(planogramId);

                // Map replacePlanogram to availablePlanograms format
                const planograms = (response?.replacePlanogram || []).map(p => ({
                    id: p?.uuid,
                    label: p?.name,
                    name: p?.name
                }));

                // Map assignedDevices to availableDevices format
                const devices = (response?.assignedDevices || []).map(d => ({
                    id: d?.uuid,
                    name: d?.name
                }));

                if (!cancelled) {
                    setAvailablePlanograms(planograms);
                    setAvailableDevices(devices);
                }
            } catch (e) {
                if (!cancelled) {
                    setAvailablePlanograms([]);
                    setAvailableDevices([]);
                }
            } finally {
                if (!cancelled) {
                    setIsLoadingPlanograms(false);
                    setIsLoadingDevices(false);
                }
            }
        };
        fetchDetails();
        return () => { cancelled = true; };
    }, [visible, planogramId]);

    // Auto-select all devices when they are loaded
    useEffect(() => {
        if (availableDevices.length > 0 && visible) {
            setFormData(prev => ({
                ...prev,
                selectedDevices: availableDevices
            }));
        }
    }, [availableDevices, visible]);

    useEffect(() => {
        if (visible) {
            setFormData(prev => ({
                ...prev,
                newPlanogram: null,
                clearCells: false,
                effectiveDate: null,
                selectedDay: false
            }));
        }
    }, [visible]);


    const handleDeactivate = async (effectiveDate) => {
        if (isSaving) {
            return;
        }
        if (!effectiveDate) {
            return;
        }
        const payload = {
            oldPlanogramExternalId: planogramId,
            newPlanogramExternalId: formData.newPlanogram?.id,
            assignedDevicesExternalIds: formData.selectedDevices.map(d => d.id),
            clearCells: formData.clearCells,
            effectiveDate: effectiveDate
        };
        
        setIsSaving(true);
        try {
            await PlanogramService.deactivateAndReplaceParentPlanogram(payload);
            showSuccessMessage(t('planogramsPage.deactivateDialog.successMessage'));
            onHide?.();
            if (onConfirm) {
                onConfirm();
            }
        } catch (error) {
            console.error('Error deactivating planogram:', error);
            showErrorMessage(t('planogramsPage.deactivateDialog.errorMessage'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleNextBusinessDay = () => {
        handleDeactivate(getNextBusinessDay());
    };

    const handleSelectedDay = () => {
        setFormData(prev => ({ ...prev, selectedDay: true }));
    };

    const handleSave = () => {
        handleDeactivate(formData.effectiveDate);
    };


    // Don't render until translations are ready
    if (!ready) {
        return null;
    }

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={t('planogram.device.deactivateDialog.title')}
            className="planogram-deactivate-dialog"
            closable={true}
            closeOnEscape={true}
            modal={true}
            draggable={false}
            resizable={false}
            focusOnShow={true}
            autoFocus={true}
            footer={
                !formData.selectedDay ? (
                    <div className="flex w-full gap-3">
                        <Button
                            label={t('planogram.device.deactivateDialog.nextBusinessDay')}
                            onClick={handleNextBusinessDay}
                            disabled={isSaving || !formData.newPlanogram || formData.selectedDevices.length === 0}
                            loading={isSaving}
                            className="flex-1 p-button-primary"
                        />
                        <Button
                            label={t('planogram.device.deactivateDialog.selectedDay')}
                            onClick={handleSelectedDay}
                            disabled={isSaving || !formData.newPlanogram || formData.selectedDevices.length === 0}
                            className="flex-1 p-button-secondary"
                        />
                    </div>
                ) : (
                    <div className="flex w-full gap-3">
                        <Button
                            label={t('common.save')}
                            onClick={handleSave}
                            disabled={isSaving || !formData.effectiveDate}
                            loading={isSaving}
                            className="flex-1 p-button-primary"
                        />
                        <Button
                            label={t('common.cancel')}
                            onClick={() => setFormData(prev => ({ ...prev, selectedDay: false, effectiveDate: null }))}
                            disabled={isSaving}
                            className="flex-1 p-button-secondary"
                        />
                    </div>
                )
            }
        >
            <div className="flex flex-column gap-4">
                {/* Planogram subtitle */}
                <div className="planogram-subtitle text-primary">
                    {planogramTitle}
                </div>

                {/* Step 1 - Only show when selectedDay is false */}
                {!formData.selectedDay && (
                    <>
                        {/* Description */}
                        <div className="text-color-secondary text-sm line-height-3 description">
                            {t('planogram.device.deactivateDialog.description')}
                        </div>

                        {/* Select new planogram */}
                        <div className="flex flex-column gap-2 w-full">
                            <label className="text-color-secondary text-sm uppercase">
                                {t('planogram.device.deactivateDialog.selectNewPlanogram')}
                            </label>
                            <Dropdown
                                id="newPlanogram"
                                dataKey="id"
                                optionLabel="label"
                                value={formData.newPlanogram}
                                options={availablePlanograms}
                                onChange={e => setFormData(prev => ({ ...prev, newPlanogram: e.value }))}
                                placeholder={t('common.select')}
                                className="w-full"
                                showClear
                                loading={isLoadingPlanograms}
                            />
                        </div>

                        {/* Devices section */}
                        <div className="flex flex-column gap-2 w-full">
                            <MultiSelect
                                label={t('planogram.device.deactivateDialog.devices')}
                                value={formData.selectedDevices}
                                onChange={(e) => setFormData(prev => ({ ...prev, selectedDevices: e.value }))}
                                options={availableDevices}
                                optionLabel="name"
                                placeholder={t('planogram.device.deactivateDialog.selectDevices')}
                                className="w-full"
                                filter
                                filterFunction={(options, search) => {
                                    return options.filter(o => o.name?.toLowerCase().includes(search.toLowerCase()));
                                }}
                                maxSelectedItemsVisible={'auto'}
                                showSelectedCount
                                loading={isLoadingDevices}
                            />
                        </div>

                        {/* Clear cells toggle */}
                        <div className="flex align-items-center gap-3">
                            <InputSwitch
                                checked={formData.clearCells}
                                onChange={e => setFormData(prev => ({ ...prev, clearCells: e.value }))}
                            />
                            <div className="flex align-items-center gap-2">
                                <span className="text-color-secondary text-sm">
                                    {t('planogram.device.deactivateDialog.clearProductsCells')}
                                </span>
                                <InfoIcon className="info-icon" />
                            </div>
                        </div>
                    </>
                )}

                {/* Step 2 - Display when "Selected day" is chosen */}
                {formData.selectedDay && (
                    <div className="flex flex-column gap-5 mt-1">
                        <div className="text-color-secondary text-sm line-height-3 m-0 description">
                            {t('planogram.deactivate.selectedDayDescription')}
                        </div>
                        <div className="flex flex-column gap-2 w-full">
                            <label className="font-semibold text-color-secondary text-sm uppercase">
                                {t('planogram.deactivate.effectiveDate')}
                            </label>
                            <Calendar
                                value={formData.effectiveDate}
                                onChange={(e) => setFormData(prev => ({ ...prev, effectiveDate: e.value }))}
                                dateFormat="dd/mm/yy"
                                showIcon
                                placeholder={t('planogram.deactivate.effectiveDatePlaceholder')}
                                className="w-full"
                            />
                        </div>
                    </div>
                )}
            </div>
        </Dialog>
    );
};

export default PlanogramDeactivateDialog;
